import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { ChatModalFooter } from './ChatFooter'
import { ChatModalHeader } from './ChatHeader'
import { ChatModalBody } from './ChatBody'
import { Post } from 'redux/reducers/feed'
import { BaseModalV2 } from 'components/ui/BaseModal'
import useComments from 'hooks/useComments'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import keys from 'constants/keys'
import { IComment } from 'constants/interfaces'
import { AnnouncementCommentModal } from 'components/Chat/AnnouncementChat/AnnouncementModal'

interface IProps {
  isOpen: boolean
  onClose: () => void
  chat: Post
  targetParentCommentId?: string | null
  targetCommentId?: string | null
}

export const ChatModal = ({ isOpen, onClose, chat, targetParentCommentId, targetCommentId }: IProps) => {
  const { post_id: chatId } = chat
  const { fetchComments, fetchCommentsLoading, commentsCounter, sendComment, sendCommentLoading } = useComments(chatId)
  const comments = useSelector((state: RootState) => state.comments.commentsState?.[chatId]?.items ?? [])
  const messages = useSelector((state: RootState) => (chatId ? state.comments.commentsState?.[chatId] : undefined))
  const { uid } = useSelector((state: RootState) => state.auth)
  const { commentContext } = useSelector((state: RootState) => state.feed)

  const [selectedMessage, setSelectedMessage] = useState<IComment | null>(null)

  const chatCreatedBy = chat?.created_by
  const chatAdmins = chat?.admins
  const chatType = chat?.type

  const isCurrentUserIsChatCreator = useMemo(() => chatCreatedBy === uid, [chatCreatedBy, uid])
  const isCurrentChatUserIsAdmin = useMemo(() => chatAdmins?.includes(uid!) ?? false, [chatAdmins, uid])

  const isShowCommentInput = useMemo(
    () =>
      chatType === keys.POST_TYPE.GROUP_POST ||
      ((isCurrentUserIsChatCreator || isCurrentChatUserIsAdmin) && chatType === keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT),
    [chatType, isCurrentUserIsChatCreator, isCurrentChatUserIsAdmin]
  )

  const isAnnouncementMode = useMemo(() => chatType === keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT, [chatType])

  const handleMessageClick = useCallback(
    (commentId: string) => {
      if (isAnnouncementMode) {
        const message = comments.find((c) => c.cid === commentId)
        if (message) {
          setSelectedMessage(message)
        }
      }
    },
    [isAnnouncementMode, comments]
  )

  const handleBackToChat = useCallback(() => {
    setSelectedMessage(null)
  }, [])

  useEffect(() => {
    if (!isAnnouncementMode || !targetParentCommentId || selectedMessage) return
    const message = comments.find((comment) => comment.cid === targetParentCommentId)
    if (message) {
      setSelectedMessage(message)
    }
  }, [comments, isAnnouncementMode, selectedMessage, targetParentCommentId])

  // Режим просмотра комментариев к сообщению
  if (selectedMessage) {
    return (
      <AnnouncementCommentModal
        chat={chat}
        message={selectedMessage}
        onBack={handleBackToChat}
        onClose={onClose}
        targetCommentId={targetCommentId}
      />
    )
  }

  // Обычный режим чата
  return (
    <BaseModalV2
      isOpen={isOpen}
      onClose={onClose}
      header={<ChatModalHeader chat={chat} onClose={onClose} />}
      modalBodyProps={{ overflow: 'hidden', py: 0, px: 0, height: '70vh' }}
      body={
        <ChatModalBody
          currentChat={chat}
          messages={messages}
          comments={comments}
          fetchComments={fetchComments}
          chatId={chatId}
          fetchCommentsLoading={fetchCommentsLoading}
          commentsCounter={commentsCounter}
          onMessageClick={isAnnouncementMode ? handleMessageClick : undefined}
          targetCommentId={targetCommentId}
        />
      }
      footer={
        <ChatModalFooter
          chat={chat}
          comments={comments}
          commentContext={commentContext}
          sendComment={sendComment}
          sendCommentLoading={sendCommentLoading}
          commentsCounter={commentsCounter}
          isShowCommentInput={isShowCommentInput}
        />
      }
    />
  )
}
